// src/pages/ContactsPage.jsx
import React, { useState, useEffect } from 'react';
import { 
  FiUsers, FiPlus, FiSearch, FiFilter, FiDownload, 
  FiUpload, FiMail, FiMessageSquare, FiMoreVertical,
  FiEdit, FiTrash2, FiEye
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import ContactList from '../components/contacts/ContactList';
import ContactForm from '../components/contacts/ContactForm';
import ContactMessaging from '../components/contacts/ContactMessaging';
import ContactHistory from '../components/contacts/ContactHistory';
import { contactsService } from '../utils/contactsUtils';

const ContactsPage = () => {
  const { userProfile } = useAuth();
  const organizationId = userProfile?.organizationId;
  
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI State
  const [showContactForm, setShowContactForm] = useState(false);
  const [showMessaging, setShowMessaging] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('all');
  
  // Load contacts on mount
  useEffect(() => {
    if (organizationId) {
      fetchContacts();
    }
  }, [organizationId]);

  // Filter contacts when search/filter changes
  useEffect(() => {
    filterContacts();
  }, [contacts, searchTerm, typeFilter, industryFilter]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const contactsData = await contactsService.getContacts(organizationId);
      setContacts(contactsData);
    } catch (err) {
      setError('Failed to load contacts');
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = () => {
    let filtered = contacts;

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(contact => 
        contact.name?.toLowerCase().includes(search) ||
        contact.email?.toLowerCase().includes(search) ||
        contact.organisation?.toLowerCase().includes(search) ||
        contact.industry?.toLowerCase().includes(search)
      );
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(contact => contact.type === typeFilter);
    }

    // Industry filter
    if (industryFilter !== 'all') {
      filtered = filtered.filter(contact => contact.industry === industryFilter);
    }

    setFilteredContacts(filtered);
  };

  const handleContactSaved = async (contactData) => {
    try {
      if (editingContact) {
        await contactsService.updateContact(organizationId, editingContact.id, contactData);
      } else {
        await contactsService.createContact(organizationId, contactData);
      }
      
      await fetchContacts();
      setShowContactForm(false);
      setEditingContact(null);
    } catch (err) {
      setError('Failed to save contact');
      console.error('Error saving contact:', err);
    }
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setShowContactForm(true);
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      await contactsService.deleteContact(organizationId, contactId);
      await fetchContacts();
    } catch (err) {
      setError('Failed to delete contact');
      console.error('Error deleting contact:', err);
    }
  };

  const handleSendEmail = () => {
    if (selectedContacts.length === 0) {
      setError('Please select at least one contact to send email');
      return;
    }
    // Navigate to email composer with selected contacts
    window.location.href = `/email/compose?contacts=${selectedContacts.join(',')}`;
  };

  const handleSendLinkedInMessage = () => {
    if (selectedContacts.length === 0) {
      setError('Please select at least one contact to send LinkedIn message');
      return;
    }
    setShowMessaging(true);
  };

  const handleViewHistory = () => {
    setShowHistory(true);
  };

  const handleImportContacts = async (file) => {
    try {
      setLoading(true);
      const importedContacts = await contactsService.importContactsFromCSV(organizationId, file);
      await fetchContacts();
      alert(`Successfully imported ${importedContacts.length} contacts`);
    } catch (err) {
      setError('Failed to import contacts');
      console.error('Error importing contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportContacts = async () => {
    try {
      await contactsService.exportContactsToCSV(filteredContacts);
    } catch (err) {
      setError('Failed to export contacts');
      console.error('Error exporting contacts:', err);
    }
  };

  // Get unique industries for filter dropdown
  const getUniqueIndustries = () => {
    const industries = contacts
      .map(contact => contact.industry)
      .filter(Boolean)
      .filter((industry, index, arr) => arr.indexOf(industry) === index);
    return industries.sort();
  };

  const getContactStats = () => {
    return {
      total: contacts.length,
      people: contacts.filter(c => c.type === 'Person').length,
      organisations: contacts.filter(c => c.type === 'Organisation').length,
      withLinkedIn: contacts.filter(c => c.linkedin).length,
      withEmail: contacts.filter(c => c.email).length
    };
  };

  const stats = getContactStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Contacts</h1>
          <p className="text-gray-600 mt-2">
            Manage your business contacts and communications
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={() => document.getElementById('import-input').click()}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center"
          >
            <FiUpload className="mr-2" size={16} />
            Import
          </button>
          
          <button
            onClick={handleExportContacts}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center"
          >
            <FiDownload className="mr-2" size={16} />
            Export
          </button>
          
          <button
            onClick={() => setShowContactForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
          >
            <FiPlus className="mr-2" size={16} />
            Add Contact
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FiUsers className="text-blue-600" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Contacts</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <FiUsers className="text-green-600" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">People</p>
              <p className="text-2xl font-bold text-gray-900">{stats.people}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <FiUsers className="text-purple-600" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Organisations</p>
              <p className="text-2xl font-bold text-gray-900">{stats.organisations}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <FiMail className="text-yellow-600" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">With Email</p>
              <p className="text-2xl font-bold text-gray-900">{stats.withEmail}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FiMessageSquare className="text-blue-600" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">With LinkedIn</p>
              <p className="text-2xl font-bold text-gray-900">{stats.withLinkedIn}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button 
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="Person">People</option>
              <option value="Organisation">Organisations</option>
            </select>
            
            <select
              value={industryFilter}
              onChange={(e) => setIndustryFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Industries</option>
              {getUniqueIndustries().map(industry => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      {selectedContacts.length > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-800">
              {selectedContacts.length} contact{selectedContacts.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleSendEmail}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
              >
                <FiMail className="mr-1" size={14} />
                Send Email
              </button>
              <button
                onClick={handleSendLinkedInMessage}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
              >
                <FiMessageSquare className="mr-1" size={14} />
                LinkedIn Message
              </button>
              <button
                onClick={handleViewHistory}
                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center"
              >
                <FiEye className="mr-1" size={14} />
                View History
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contacts List */}
      <ContactList
        contacts={filteredContacts}
        selectedContacts={selectedContacts}
        onSelectionChange={setSelectedContacts}
        onEditContact={handleEditContact}
        onDeleteContact={handleDeleteContact}
        loading={loading}
      />

      {/* Hidden file input for import */}
      <input
        id="import-input"
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={(e) => e.target.files[0] && handleImportContacts(e.target.files[0])}
        className="hidden"
      />

      {/* Modals */}
      {showContactForm && (
        <ContactForm
          contact={editingContact}
          onSave={handleContactSaved}
          onClose={() => {
            setShowContactForm(false);
            setEditingContact(null);
          }}
        />
      )}

      {showMessaging && (
        <ContactMessaging
          contacts={selectedContacts.map(id => contacts.find(c => c.id === id)).filter(Boolean)}
          onClose={() => setShowMessaging(false)}
          onMessageSent={() => {
            setShowMessaging(false);
            setSelectedContacts([]);
          }}
        />
      )}

      {showHistory && (
        <ContactHistory
          contacts={selectedContacts.map(id => contacts.find(c => c.id === id)).filter(Boolean)}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
};

export default ContactsPage;